import { Typography } from '../../components/ui/Typography';
import { CheckCircleIcon, EmptyCircleIcon } from '../../components/ui/icons';
import { AiGeneratedBadge } from '../../components/ui/AiGeneratedBadge';
import { buildContentStatus } from './contentStatus';
import type { ContentStatusInput, ContentStatusItem } from './contentStatus';

interface ContentStatusRowProps {
  item: ContentStatusItem;
}

const ContentStatusRow = ({ item }: ContentStatusRowProps) => (
  <li className="flex items-center gap-2 text-sm">
    {item.isSet ? (
      <CheckCircleIcon className="h-3 w-3 flex-shrink-0 text-primary-700" />
    ) : (
      <EmptyCircleIcon className="h-3 w-3 flex-shrink-0 text-text-muted" />
    )}
    <span className={item.isSet ? 'text-text-secondary' : 'text-text-muted'}>{item.label}</span>
    <span className="sr-only">{item.isSet ? 'set' : 'not set'}</span>
    {item.isSet && item.detail && (
      <span className="text-text-muted text-xs">({item.detail})</span>
    )}
    {item.isAiGenerated && (
      <AiGeneratedBadge
        className="ml-auto"
        title={`${item.label} was generated by AI from public sources and has not been reviewed by the venue.`}
      />
    )}
  </li>
);

interface ContentStatusPanelProps {
  venue: ContentStatusInput;
}

/**
 * Completion readout for a venue's content-bearing fields. Grouped to match the
 * sections of the venue edit form, so a thin group tells an admin which part of
 * the form to open.
 */
export const ContentStatusPanel = ({ venue }: ContentStatusPanelProps) => {
  const status = buildContentStatus(venue);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <Typography variant="label" as="h3" className="text-text-primary">
          Content Status
        </Typography>
        <span className="text-sm text-text-secondary tabular-nums">
          {status.percentComplete}% complete
        </span>
      </div>

      <div
        className="h-1 w-full rounded-full bg-gray-200 overflow-hidden mb-4"
        role="progressbar"
        aria-label="Venue content completeness"
        aria-valuenow={status.percentComplete}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-primary-700 rounded-full transition-[width] duration-300 motion-reduce:transition-none"
          style={{ width: `${status.percentComplete}%` }}
        />
      </div>

      <div className="space-y-4">
        {status.groups.map((group) => (
          <div key={group.key}>
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <Typography variant="caption" as="span" className="text-text-secondary uppercase tracking-wide">
                {group.label}
              </Typography>
              <span className="text-xs text-text-muted tabular-nums">
                {group.setCount}/{group.total}
              </span>
            </div>
            <ul className="space-y-1">
              {group.items.map((item) => (
                <ContentStatusRow key={item.key} item={item} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};
